import { isNil, get, has, keys, without } from 'lodash';
import { Meteor } from 'meteor/meteor';

/**
 * Gets the id of the user from the user object.
 *
 * @param {object} user - The user object.
 * @returns {string} The user id or an empty string if not found.
 * @throws {Error} If user is null or undefined.
 */
export const getId = user => {
  if (isNil(user)) {
    throw new Error('user not defined');
  }
  return get(user, '_id', '');
};

/**
 * Check the type of user login, if it's oauth or password.
 *
 * @param {object} user - The user object.
 * @returns {string} Password for normal login, oauth for oauth users.
 * @throws {Error} If user is null or undefined.
 */
export const getUserType = user => {
  if (isNil(user)) {
    throw new Error('user not defined');
  }
  const hasEmailsKey = has(user, 'emails');
  if (hasEmailsKey) {
    return 'password';
  }
  return 'oauth';
};

/**
 * Check if the user is logged in via oauth.
 *
 * @param {object} user - The user object.
 * @returns {boolean} True if the user is oauth and false otherwise.
 * @throws {Error} If user is null or undefined.
 */
export const isOauthUser = user => {
  if (isNil(user)) {
    throw new Error('user not defined');
  }
  return getUserType(user) === 'oauth';
};

/**
 * Check if the user is logged in via email.
 *
 * @param {object} user - The user object.
 * @returns {boolean} True if the user is email use or false otherwise.
 * @throws {Error} If user is null or undefined.
 */
export const isPasswordUser = user => {
  if (isNil(user)) {
    throw new Error('user not defined');
  }
  return getUserType(user) === 'password';
};

/**
 * Gets the name of the user from the user object. Depeding on how the user was created
 * (oauth or email) the name on the profile will be a string or an object.
 *
 * @param {object} user - The user object.
 * @returns {string} The name of the user in the format 'firstName lastName'.
 * @throws {Error} If user is null or undefined.
 */
export const getUserName = user => {
  if (isNil(user)) {
    throw new Error('user not defined');
  }
  const userName = get(user, 'profile.name');
  return {
    string: userName,
    object: `${userName.first} ${userName.last}`
  }[typeof userName];
};

/**
 * Gets the first name of the user from the user object. Only works for passoword users.
 *
 * @param {object} user - The user object.
 * @returns {string} The first name of the user or an empty string if not found.
 * @throws {Error} If user is null or undefined.
 */
export const getFirstName = user => {
  if (isNil(user)) {
    throw new Error('user not defined');
  }
  return get(user, 'profile.name.first', '');
};

/**
 * Gets the last name of the user from the user object. Only works for passoword users.
 *
 * @param {object} user - The user object.
 * @returns {string} The last name of the user or an empty string if not found.
 * @throws {Error} If user is null or undefined.
 */
export const getLastName = user => {
  if (isNil(user)) {
    throw new Error('user not defined');
  }
  return get(user, 'profile.name.last', '');
};

/**
 * Get the Oauth service for the user.
 *
 * @param {object} user - The user object.
 * @returns {string} The Oauth service the user logged in with or an empty string if something goes wrong.
 * @throws {Error} If user is null or undefined.
 */
export const getOauthService = user => {
  if (isNil(user)) {
    throw new Error('user not defined');
  }
  if (isOauthUser(user)) {
    const userService = keys(user.services);
    const cleanUserService = without(userService, 'resume');
    return cleanUserService[0];
  }
  return '';
};

/**
 * Checks if User is logged in via Esri.
 *
 * @param {object} user - The user object.
 * @returns {string} True if the user is logged in via esri, false otherwise.
 * @throws {Error} If user is null or undefined.
 */
export const isEsriUser = user => {
  if (isNil(user)) {
    throw new Error('user not defined');
  }
  return getOauthService(user) === 'esri';
};

/**
 * Checks if the user has a services object.
 *
 * @param {object} user - The user object.
 * @returns {boolean} True if the the services object exists for that user.
 * @throws {Error} If user is null or undefined
 * this object is only available from the server, the profile and for user management.
 */
export const hasServicesObject = user => {
  if (isNil(user)) {
    throw new Error('user not defined');
  }
  return has(user, 'services');
};

/**
 * Gets the email address from the user object.
 *
 * @param {object} user - The user object.
 * @returns {string} The email address of the user or an empty string if something goes wrong.
 * @throws {Error} If user is null or undefined.
 * FIXME: check how this works with oauth users.
 */
export const getEmailAddress = user => {
  if (isNil(user)) {
    throw new Error('user not defined');
  }
  if (isPasswordUser(user)) {
    return get(user, 'emails[0].address', '');
  }
  if (isOauthUser(user) && hasServicesObject(user)) {
    const userService = getOauthService(user);
    return get(user, `services[${userService}].email`, '');
  }
  return '';
};

/**
 * Checks if the email of the user is verified or not. Always returns true for oauth users.
 *
 * @param {object} user - The user object.
 * @returns {boolean} True is the email is verified or the user is oauth and false otherwise.
 * @throws {Error} If user is null or undefined.
 */
export const isEmailVerified = user => {
  if (isNil(user)) {
    throw new Error('user not defined');
  }
  if (isOauthUser(user)) {
    return true;
  }
  if (isPasswordUser(user)) {
    return get(user, 'emails[0].verified', false);
  }
  return false;
};

/**
 * Returns the user data from the object that google sends back.
 *
 * @param {object} service - The user service object.
 * @returns {object} An object containing the user data, email and name.
 */
export const parseGoogleData = service => ({
  service: 'google',
  emails: [{ address: service.email }],
  profile: {
    name: {
      first: service.given_name,
      last: service.family_name
    }
  }
});

/**
 * Returns the user data from the object that facebook sends back.
 *
 * @param {object} service - The user service object.
 * @returns {object} An object containing the user data, email and name.
 */
export const parseFacebookData = service => ({
  service: 'facebook',
  emails: [{ address: service.email }],
  profile: {
    name: {
      first: service.first_name,
      last: service.last_name
    }
  }
});

/**
 * Returns the user data from the services object.
 *
 * @param {object} services - The user service object.
 * @returns {?object} An object containing the user data, email and name.
 */
export const getDataForService = services => {
  if (services.facebook) return parseFacebookData(services.facebook);
  if (services.google) return parseGoogleData(services.google);
  return null;
};

/**
 * Returns the user profile from the user object.
 *
 * @param {object} user - The user object.
 * @returns {object} The user profile or an empty object.
 * FIXME: revisar si usar esto en funcion de con que gestor de usuarios nos quedamos.
 */
export const getUserProfile = user => {
  if (isNil(user)) {
    return {};
  }
  const isOAuth = !hasServicesObject(user) ? false : !user.services.password;
  // If services do not exist, it's the current user.
  const userData = isOAuth
    ? { _id: user._id, ...getDataForService(user.services) }
    : { service: 'password', ...user };
  return userData;
};

/**
 * Returns the user settings.
 *
 * @param {object} key - The setting for that user for which we are retriving a value.
 * @param {boolean} valueOnly - True if you only want the value for the setting.
 * @param {boolean} notCurrentUserId - User id if we are not looking for the current user settings.
 * @returns {?object} The user profile.
 */
// FIXME: Revisar porque no se usa esto en ningun lado.
export const getUserSettings = (key, valueOnly, notCurrentUserId) => {
  if (!key) {
    console.warn('[Ipsilum] Please pass a setting key to retrieve.');
    return null;
  }
  const userId = notCurrentUserId || Meteor.userId();
  if (userId && key) {
    const user = Meteor.users.findOne({ _id: userId });
    const foundSetting =
      user && user.settings ? user.settings.find(userSetting => userSetting.key === key) : null;
    if (foundSetting) {
      return valueOnly ? foundSetting.value : foundSetting;
    }
    return null;
  }
  return null;
};

/**
 * Gets the user storage cuota object.
 *
 * @param {object} user - The user object.
 * @returns {object} The storege cuota object for that user, which includes the storage size limit
 * and the used storage, or empty object if not found.
 * @throws {Error} If user is null or undefined.
 * This object is only available from the server, the profile and for user management.
 */
export const getStorageCuota = user => {
  if (isNil(user)) {
    throw new Error('user not defined');
  }
  return get(user, 'storageCuota', {});
};

/**
 * Gets the storage size limit for the user.
 *
 * @param {object} user - The user object.
 * @returns {number} The storage size limit for that user, or 0 if not found.
 * @throws {Error} If user is null or undefined.
 */
export const getSizeLimit = user => {
  if (isNil(user)) {
    throw new Error('user not defined');
  }
  return get(user, 'storageCuota.sizeLimit', 0);
};

/**
 * Gets the used storage for the user.
 *
 * @param {object} user - The user object.
 * @returns {number} The used storage for that user, or 0 if not found.
 * @throws {Error} If user is null or undefined.
 */
export const getUsedStorage = user => {
  if (isNil(user)) {
    throw new Error('user not defined');
  }
  return get(user, 'storageCuota.usedStorage', 0);
};

/**
 * Gets the id of the default base map from the user object.
 *
 * @param {object} user - The user object.
 * @returns {string} The id of the default base map or an empty string if not found.
 * @throws {Error} If user is null or undefined.
 */
export const getDefaultBaseMap = user => {
  if (isNil(user)) {
    throw new Error('user not defined');
  }
  return get(user, 'profile.defaultBaseMap', '');
};

/**
 * Gets the selected feature color from the user object.
 *
 * @param {object} user - The user object.
 * @returns {string} The selected feature color or a default color if not found.
 * @throws {Error} If user is null or undefined.
 */
export const getFeatureSelectedColor = user => {
  if (isNil(user)) {
    throw new Error('user not defined');
  }
  return get(user, 'profile.featureSelectedColor', '#666');
};

/**
 * Gets the selected feature weight from the user object.
 *
 * @param {object} user - The user object.
 * @returns {number} The selected feature weight or 9 if not found.
 * @throws {Error} If user is null or undefined.
 */
export const getFeatureSelectedWeight = user => {
  if (isNil(user)) {
    throw new Error('user not defined');
  }
  return get(user, 'profile.featureSelectedWeight', 9);
};
